import { useParams, useNavigate } from 'react-router-dom';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Progress,
  Row,
} from 'reactstrap';
import LoadingCard from '../../components/LoadingCard';
import PropertyDetailCard from '../../components/PropertyDetailCard';
import { useAdminProperty } from './hooks/useAdminProperty';
import HeaderCounters from './components/AdminProperty/HeaderCounters';
import LandlordVerifiedCard from './components/AdminProperty/LandlordVerifiedCard';
import usePropertyCounterViews from './hooks/usePropertyCounterViews';
import {
  getPropertySaleStatus,
  getPropertyStatus,
  getPublicPropertyName,
  getStatusBadgeColor,
} from './adapters/propertyAdapter';
import useAuth from '../../hooks/useAuth';
import FloatingChat from '../../components/chat/FloatingChat';
import {
  PROPERTY_SALE_STATUS_VALUES,
  PROPERTY_STATUS_VALUES,
} from './constants/propertyConstants';

const AdminProperty = () => {
  const { id = '' } = useParams();
  const { isPermissionAvailable } = useAuth();
  const getProgressBarColor = (percentage: number) => {
    if (percentage > 60) {
      return 'success';
    } else if (percentage > 40) {
      return 'warning';
    } else {
      return 'danger';
    }
  };
  const navigate = useNavigate();
  const { isLoading, adminPropertyData, changeIsVerified } =
    useAdminProperty(id);
  const { isLoading: isLoadingCounters, lastCounter } =
    usePropertyCounterViews(id);

  if (isLoading || !adminPropertyData) {
    return <LoadingCard name="" parent_name="" />;
  }

  const rentStatus = adminPropertyData.status ?? '';
  const saleStatus = adminPropertyData.sale_status ?? '';

  const sections = [
    {
      icon: 'fa-solid fa-circle-info',
      color: '#5e72e4',
      title: 'Información adicional',
      description: 'Propietario, agente, nombre de propiedad, descripción',
      path: `/properties/admin/${id}/additional-info`,
    },
    {
      icon: 'fa-solid fa-location-dot',
      color: '#f5365c',
      title: 'Dirección y ubicación',
      description: 'Ubicación GPS y asignación de polígono/residencial/torre',
      path: `/properties/admin/${id}/address-location`,
    },
    {
      icon: 'fa-solid fa-couch',
      color: '#8965e0',
      title: 'Amenidades y características',
      description: 'Habitaciones, baños, área, línea blanca, amueblado, mascotas',
      path: `/properties/admin/${id}/amenities`,
    },
    {
      icon: 'fa-solid fa-key',
      color: '#11cdef',
      title: 'Configurar información de renta',
      description: 'Precios de renta, estados en marketplace de rentas',
      badge: {
        label: rentStatus ? getPropertyStatus(rentStatus) : 'No definido',
        color: getStatusBadgeColor(rentStatus),
      },
      path: `/properties/admin/${id}/rent`,
    },
    {
      icon: 'fa-solid fa-tag',
      color: '#2dce89',
      title: 'Configurar información de venta',
      description:
        'Precios de venta, estados en marketplace de ventas, información de escrituración, carta extractada',
      badge: {
        label: getPropertySaleStatus(saleStatus),
        color: getStatusBadgeColor(saleStatus),
      },
      path: `/properties/admin/${id}/sales`,
    },
    {
      icon: 'fa-solid fa-images',
      color: '#fb6340',
      title: 'Administrar contenido y fotografías',
      description: 'Cover, galería, matterport, link de youtube',
      path: `/properties/admin/${id}/photos`,
    },
    {
      icon: 'fa-solid fa-user-tie',
      color: '#5e72e4',
      title: 'Configurar agente inmobiliario',
      path: `/properties/admin/${id}/broker`,
    },
    ...(isPermissionAvailable('SHOW_MARKETPLACE_SYNC')
      ? [
          {
            icon: 'fa-solid fa-rotate',
      color: '#32325d',
            title: 'Sincronizar con marketplace',
            path: `/sync-marketplace/${id}/property`,
          },
        ]
      : []),
  ];

  return (
    <>
      <HeaderCounters
        property_id={id}
        parentName="Propiedades"
        name={`Administrar propiedad #${adminPropertyData.code}`}
        offer_count={adminPropertyData.offers}
        visit_count={adminPropertyData.visits}
        visualization_text={lastCounter.text}
        visualization_count={lastCounter.counter}
      />
      <Container className="mt--5" fluid>
        <Card>
          <CardHeader>
            <Row>
              <Col md="6">
                <PropertyDetailCard
                  cover={adminPropertyData.cover}
                  address={adminPropertyData.address}
                  code={adminPropertyData.code}
                  name={getPublicPropertyName(adminPropertyData)}
                  price={adminPropertyData.monthly_price}
                  sale_price={adminPropertyData.sale_monthly_price}
                />
              </Col>
              <Col md="6">
                <LandlordVerifiedCard
                  is_validated_by_proper={
                    adminPropertyData.is_validated_by_proper ?? false
                  }
                  validated_by_proper_event={changeIsVerified}
                  landlord_name={
                    [
                      adminPropertyData.landlordInfo?.first_name,
                      adminPropertyData.landlordInfo?.last_name,
                    ]
                      .filter(Boolean)
                      .join(' ') ||
                    adminPropertyData.landlordInfo?.name ||
                    ''
                  }
                  landlord_email={adminPropertyData.landlordInfo?.email ?? ''}
                  landlord_id={adminPropertyData.landlord_id ?? ''}
                  property_id={id}
                />
              </Col>
            </Row>
          </CardHeader>
          <CardHeader className="py-0">
            <div className="progress-wrapper">
              <div className="progress-info">
                <div className="progress-label">
                  <span>
                    {adminPropertyData.admin_data_progress >= 100
                      ? 'Información completa'
                      : 'Información de la propiedad completada'}
                  </span>
                </div>
                <div className="progress-percentage">
                  <span>{adminPropertyData.admin_data_progress}%</span>
                </div>
              </div>
              <Progress
                max="100"
                value={adminPropertyData.admin_data_progress}
                color={getProgressBarColor(
                  adminPropertyData.admin_data_progress,
                )}
              />
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              {sections.map(
                ({ icon, color, title, description, badge, path }) => (
                  <Col lg="6" key={path}>
                    <a
                      href={path}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(path);
                      }}
                      className="list-group-item list-group-item-action rounded mb-3 py-3 d-block"
                    >
                      <Row className="align-items-center">
                        <Col className="col-auto">
                          <div
                            className="icon icon-shape icon-sm rounded-circle shadow-none"
                            style={{
                              backgroundColor: `${color}1f`,
                              color,
                            }}
                          >
                            <i className={icon} />
                          </div>
                        </Col>
                        <div className="col ml--2">
                          <h4 className="mb-0">{title}</h4>
                          {description && (
                            <small style={{ color: '#525f7f' }}>
                              {description}
                            </small>
                          )}
                        </div>
                        <Col className="col-auto">
                          {badge && (
                            <Badge color={badge.color} className="mr-3">
                              {badge.label}
                            </Badge>
                          )}
                          <i className="ni ni-bold-right text-muted" />
                        </Col>
                      </Row>
                    </a>
                  </Col>
                ),
              )}
            </Row>
          </CardBody>
        </Card>
      </Container>
      {(() => {
        const availableTypes: string[] = [];
        if (adminPropertyData.status === PROPERTY_STATUS_VALUES.PUBLISHED)
          availableTypes.push('for_rent');
        if (
          adminPropertyData.sale_status ===
          PROPERTY_SALE_STATUS_VALUES.PUBLISHED
        )
          availableTypes.push('for_sale');
        return availableTypes.length > 0 ? (
          <FloatingChat
            propertyCode={String(adminPropertyData.code)}
            availableTypes={availableTypes}
          />
        ) : null;
      })()}
    </>
  );
};

export default AdminProperty;
